import React, { useEffect, useState } from "react";
import client, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Loader2, Save, Smartphone } from "lucide-react";

interface Rotation { provider: string; age_days: number | null; needs_rotation: boolean }
interface SecStatus {
  mfa_enabled: boolean;
  password_age_days: number | null;
  password_expires_in_days: number | null;
  password_policy: string;
  secret_rotations: Rotation[];
  secret_max_age_days: number;
}

export const SecurityPanel: React.FC = () => {
  const [status, setStatus] = useState<SecStatus | null>(null);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [setup, setSetup] = useState<{ secret: string; otpauth_url: string } | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [backup, setBackup] = useState<string[] | null>(null);
  const [disableCode, setDisableCode] = useState("");

  const load = () => client.get("/security/status").then((r) => setStatus(r.data));
  useEffect(() => { load(); }, []);

  const changePassword = async () => {
    setSavingPw(true);
    try {
      await client.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Contraseña actualizada");
      setCur(""); setNext(""); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSavingPw(false); }
  };

  const startSetup = async () => {
    try {
      const r = await client.post("/auth/mfa/setup");
      setSetup(r.data);
    } catch (err) { toast.error(apiError(err)); }
  };

  const enableMfa = async () => {
    try {
      const r = await client.post("/auth/mfa/enable", { code: mfaCode });
      setBackup(r.data.backup_codes);
      setSetup(null); setMfaCode("");
      toast.success("MFA activado");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const disableMfa = async () => {
    try {
      await client.post("/auth/mfa/disable", { code: disableCode });
      setDisableCode("");
      toast.success("MFA desactivado");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!status) return null;

  return (
    <div className="mt-6 max-w-2xl border border-slate-200 bg-white p-6" data-testid="security-panel">
      <div className="mb-5 flex items-center gap-3 border-b border-slate-100 pb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-900">
          <ShieldCheck className="h-5 w-5 text-white" strokeWidth={1.5} />
        </div>
        <div>
          <div className="font-semibold text-slate-800">Seguridad y cumplimiento</div>
          <div className="text-xs text-slate-400">Requisitos Amazon SP-API</div>
        </div>
      </div>

      {/* Estado */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="border border-slate-200 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">MFA</div>
          <div className={`mt-1 text-sm font-medium ${status.mfa_enabled ? "text-emerald-600" : "text-amber-600"}`} data-testid="mfa-status">
            {status.mfa_enabled ? "Activado" : "Desactivado"}
          </div>
        </div>
        <div className="border border-slate-200 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Vence contraseña en</div>
          <div className="mt-1 text-sm font-medium text-slate-800">
            {status.password_expires_in_days != null ? `${status.password_expires_in_days} días` : "—"}
          </div>
        </div>
        <div className="border border-slate-200 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rotación secretos</div>
          <div className="mt-1 text-sm font-medium text-slate-800">
            {status.secret_rotations.some((r) => r.needs_rotation) ? "Requiere rotación" : "Al día"}
          </div>
        </div>
      </div>

      {/* Cambio de contraseña */}
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
          <KeyRound className="h-4 w-4" strokeWidth={1.5} /> Cambiar contraseña
        </div>
        <p className="mb-3 text-xs text-slate-400">{status.password_policy}</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs text-slate-500">Contraseña actual</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="change-pw-current" className="mt-1.5 rounded-none border-slate-300" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Nueva contraseña</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="change-pw-new" className="mt-1.5 rounded-none border-slate-300" />
          </div>
        </div>
        <button onClick={changePassword} disabled={savingPw || !cur || !next} data-testid="change-pw-submit"
          className="mt-3 flex items-center gap-2 rounded-none bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700 disabled:opacity-60">
          {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.5} />}
          Actualizar contraseña
        </button>
      </div>

      {/* MFA */}
      <div className="border-t border-slate-100 pt-5">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Smartphone className="h-4 w-4" strokeWidth={1.5} /> Autenticación en dos pasos (MFA)
        </div>
        {!status.mfa_enabled ? (
          !setup ? (
            <button onClick={startSetup} data-testid="mfa-setup-btn"
              className="rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000]">
              Activar MFA
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Agrega esta clave en tu app (Google Authenticator / Authy) e ingresa el código de 6 dígitos:
              </p>
              <code className="block break-all border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700" data-testid="mfa-secret">{setup.secret}</code>
              <div className="flex gap-2">
                <Input value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder="123456" data-testid="mfa-enable-code"
                  className="w-40 rounded-none border-slate-300 tracking-[0.3em]" />
                <button onClick={enableMfa} data-testid="mfa-enable-btn"
                  className="rounded-none bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">
                  Confirmar
                </button>
              </div>
            </div>
          )
        ) : (
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-xs text-slate-500">Código MFA para desactivar</Label>
              <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="123456" data-testid="mfa-disable-code"
                className="mt-1.5 w-40 rounded-none border-slate-300 tracking-[0.3em]" />
            </div>
            <button onClick={disableMfa} data-testid="mfa-disable-btn"
              className="rounded-none border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Desactivar MFA
            </button>
          </div>
        )}

        {backup && (
          <div className="mt-4 border border-amber-200 bg-amber-50 p-3" data-testid="mfa-backup-codes">
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-amber-700">
              Guarda estos códigos de respaldo (se muestran una sola vez)
            </div>
            <div className="grid grid-cols-2 gap-1 font-mono text-sm text-slate-800 sm:grid-cols-4">
              {backup.map((c) => <span key={c}>{c}</span>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityPanel;
